// components/CombinedBarChart.tsx
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { PrincipleChartData } from "../../core/_models";

interface Props {
    principleChartData: PrincipleChartData[];
    subPrincipleWeights: Record<string, number>;
    principleColors: Record<string, string>;
    chartHeight?: number;
}

const CombinedBarChart = ({
    principleChartData,
    subPrincipleWeights,
    principleColors,
    chartHeight = 450,
}: Props) => {
    // Build the sub-principle map
    const subPrincipleMap: Record<string, Record<string, { sum: number; count: number }>> = {};
    const subPrincipleToPrinciple: Record<string, string> = {};
    const missingDataCount: Record<string, number> = {};

    principleChartData.forEach((principle) => {
        const dataSource = principle.chart_data_eval || [];

        dataSource.forEach((evaluator) => {
            const evaluatorName = evaluator.user_name || "Unknown Evaluator";
            if (evaluatorName === "Average") {
                return;
            }

            if (evaluator.labels && evaluator.data) {
                evaluator.labels.forEach((label, index) => {
                    const subPrinciple = label;
                    const value = evaluator.data[index];

                    // Skip missing values entirely
                    if (typeof value !== 'number' || isNaN(value)) {
                        // Track missing data for logging
                        const key = `${evaluatorName}||${subPrinciple}`;
                        missingDataCount[key] = (missingDataCount[key] || 0) + 1;
                        return;
                    }

                    // Map sub-principle to its principle
                    if (!subPrincipleToPrinciple[subPrinciple]) {
                        subPrincipleToPrinciple[subPrinciple] = principle.name;
                    }

                    if (!subPrincipleMap[subPrinciple]) {
                        subPrincipleMap[subPrinciple] = {};
                    }

                    if (!subPrincipleMap[subPrinciple][evaluatorName]) {
                        subPrincipleMap[subPrinciple][evaluatorName] = { sum: 0, count: 0 };
                    }
                    subPrincipleMap[subPrinciple][evaluatorName].sum += value;
                    subPrincipleMap[subPrinciple][evaluatorName].count += 1;
                });
            }
        });
    });

    // Compute categories and evaluator names
    const categories = Object.keys(subPrincipleMap);
    const evaluatorNames = new Set<string>();
    Object.values(subPrincipleMap).forEach((data) => {
        Object.keys(data).forEach((name) => {
            evaluatorNames.add(name);
        });
    });

    // Build series data
    const series = Array.from(evaluatorNames).map((evaluatorName) => {
        const data = categories.map((subPrinciple) => {
            const stats = subPrincipleMap[subPrinciple]?.[evaluatorName];

            if (stats && stats.count > 0) {
                // Return the average of valid scores only
                return Number((stats.sum / stats.count).toFixed(2));
            }

            // No valid scores for this evaluator/sub-principle
            return null; // Use null to indicate "no data"
        });
        return {
            name: evaluatorName,
            data: data,
        };
    });

    const xAxisTitle = "Sub Principles";

    // Get color for a sub-principle based on its principle
    const getColorForSubPrinciple = (subPrinciple: string): string => {
        const principleName = subPrincipleToPrinciple[subPrinciple] || '';
        return principleColors[principleName] || '#888888';
    };

    // Generate colors for each category (sub-principle)
    const categoryColors = categories.map((subPrinciple) =>
        getColorForSubPrinciple(subPrinciple)
    );

    const options: ApexOptions = {
        chart: {
            type: "bar",
            height: chartHeight,
            background: 'transparent',
            toolbar: {
                tools: {
                    download: true,
                },
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "50%",
                borderRadius: 4,
                borderRadiusApplication: "end",
                distributed: false,
            },
        },
        colors: [
            ({ dataPointIndex }: any) => categoryColors[dataPointIndex] || '#888888',
        ], xaxis: {
            categories: categories,
            title: {
                text: xAxisTitle,
                style: {
                    fontSize: '13px',
                    color: '#333',
                },
                offsetY: 10,
            },
            labels: {
                style: {
                    fontSize: '11px',
                    colors: categoryColors,
                },
            },
            crosshairs: {
                show: false,
            },
        },
        yaxis: {
            title: {
                text: "Score",
                style: {
                    fontSize: '13px',
                    color: '#333',
                },
            },
        },
        legend: {
            position: "bottom",
            horizontalAlign: "center",
            fontSize: '12px',
            fontWeight: 600,
            markers: {
                size: 12,
                strokeWidth: 0,
                offsetX: 0,
                offsetY: 0,
            },
        },
        tooltip: {
            shared: false,
            intersect: true,

            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                const subPrinciple = categories[dataPointIndex];
                const weightedScore = series[seriesIndex][dataPointIndex];

                // Check if weight exists without defaulting to 0
                const totalWeight = subPrincipleWeights[subPrinciple];
                const weightText = totalWeight !== undefined
                    ? `${totalWeight}%`
                    : "N/A";

                // Check if score is valid
                const scoreText = weightedScore !== null && weightedScore !== undefined
                    ? Number(weightedScore).toFixed(2)
                    : "No data";

                return `
      <div style="padding:10px; min-width:180px;">
        <div style="font-weight:600; margin-bottom:6px;">
          ${subPrinciple}
        </div>
        <div style="margin-bottom:4px;">
          <span style="font-weight:600;">Weighted Score:</span>
          ${scoreText}
        </div>
        <div>
          <span style="font-weight:600;">Total Weight:</span>
          ${weightText}
        </div>
      </div>
    `;
            },
        },
        grid: {
            padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10,
            },
        },
        noData: {
            text: 'No data available',
            align: 'center',
            verticalAlign: 'middle',
            style: {
                fontSize: '14px',
                color: '#999',
            },
        },
        dataLabels: {
            enabled: false,
        },
    };

    return (
        <div style={{
            width: '100%',
            height: '100%',
            margin: '0 auto',
        }}>
            <Chart
                options={options}
                series={series}
                type="bar"
                height={chartHeight}
            />
        </div>
    );
};

export default CombinedBarChart;